import React, { useState, useMemo, useCallback } from "react";
import { Search } from "react-feather";
import { Clinic } from "../../../types";
import { Button } from "../Button";
import { Dialog } from "../Dialog";
import { Fieldset } from "../Fieldset";
import { Form } from "../Form";
import { Label } from "../Label";
import { TextInput } from "../TextInput";
import { useAllClinics } from "../../../data/queries/useAllClinics";

type ManageClinicsDialogProps = {
  item: {
    id: string;
    name: string;
    visibleToClinics: Clinic[];
  };
  itemType: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (itemId: string, clinicIds: string[]) => Promise<void>;
};

export const ManageClinicsDialog = ({
  item,
  itemType,
  open,
  onOpenChange,
  onSave,
}: ManageClinicsDialogProps) => {
  const { data: clinicsData, loading: clinicsLoading } = useAllClinics();

  const [selectedClinicIds, setSelectedClinicIds] = useState<Set<string>>(
    new Set(item.visibleToClinics.map((clinic) => clinic.id)),
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredClinics = useMemo(() => {
    const allClinics = clinicsData?.allClinics || [];
    if (!searchQuery.trim()) return allClinics;
    const query = searchQuery.toLowerCase();
    return allClinics.filter(
      (clinic: Clinic) =>
        clinic.name.toLowerCase().includes(query) || clinic.id.toLowerCase().includes(query),
    );
  }, [clinicsData?.allClinics, searchQuery]);

  const handleClinicToggle = (clinicId: string) => {
    const newSelectedIds = new Set(selectedClinicIds);
    if (newSelectedIds.has(clinicId)) {
      newSelectedIds.delete(clinicId);
    } else {
      newSelectedIds.add(clinicId);
    }
    setSelectedClinicIds(newSelectedIds);
  };

  const handleSave = useCallback(async () => {
    setIsUpdating(true);
    await onSave(item.id, Array.from(selectedClinicIds));
    setIsUpdating(false);
    onOpenChange(false);
  }, [onSave, item.id, selectedClinicIds, onOpenChange]);

  const handleCancel = useCallback(() => {
    setSelectedClinicIds(new Set(item.visibleToClinics.map((clinic) => clinic.id)));
    setSearchQuery("");
    onOpenChange(false);
  }, [item.visibleToClinics, onOpenChange]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.currentTarget.value);
  }, []);

  return (
    <Dialog.Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content contentClassName="w-full max-w-lg">
        <Dialog.Title>Manage Visibility</Dialog.Title>
        <Dialog.Description>
          Select which clinics can see &ldquo;{item.name}&rdquo;
        </Dialog.Description>

        <div className="mt-5">
          <Form>
            <Fieldset>
              <Label htmlFor="clinic-search">Search Clinics</Label>
              <TextInput
                id="clinic-search"
                placeholder="Search by clinic name or ID..."
                value={searchQuery}
                onValueChange={handleSearchChange}
                icon={
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                }
              />
            </Fieldset>

            <Fieldset>
              <Label htmlFor="clinic-list">Clinic Selection</Label>
              {clinicsLoading ? (
                <div className="text-center py-8 text-gray-500">Loading clinics...</div>
              ) : (
                <>
                  {selectedClinicIds.size === 0 && (
                    <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <div className="flex items-center">
                        <div className="text-yellow-800 text-sm">
                          <strong>Warning:</strong> This {itemType} is currently not visible to any
                          clinics. Select at least one clinic to make it accessible.
                        </div>
                      </div>
                    </div>
                  )}
                  <div
                    id="clinic-list"
                    className="border border-gray-300 rounded-md max-h-60 overflow-y-auto bg-gray-50">
                    {filteredClinics.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        {searchQuery.trim()
                          ? "No clinics found matching your search."
                          : "No clinics available."}
                      </div>
                    ) : (
                      <div className="p-2">
                        {filteredClinics.map((clinic: Clinic) => (
                          <label
                            key={clinic.id}
                            className="flex items-center space-x-3 cursor-pointer hover:bg-gray-100 rounded p-2 transition-colors">
                            <input
                              type="checkbox"
                              checked={selectedClinicIds.has(clinic.id)}
                              onChange={() => handleClinicToggle(clinic.id)}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-gray-900">
                                {clinic.name}
                              </span>
                              <span className="text-xs text-gray-500">ID: {clinic.id}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </Fieldset>
          </Form>
        </div>

        <Dialog.Footer>
          <Dialog.Close>
            <Button
              variant="primary"
              text={isUpdating ? "Saving..." : "Save Changes"}
              onClick={handleSave}
              disabled={isUpdating || clinicsLoading}
            />
          </Dialog.Close>
          <Dialog.Close>
            <Button variant="outline" text="Cancel" onClick={handleCancel} disabled={isUpdating} />
          </Dialog.Close>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Dialog>
  );
};
